import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useToggle } from 'react-use-v13';
import type { ImageQueryFilters, MultimodalAssetDto } from '@scale/mm-shared';
import { ImageDatabaseSource, ImageCategory } from '@scale/mm-shared';
import {
  Button,
  TextField,
  CheckboxProps,
  Icon,
  Tag,
  Switch,
  SegmentedButtons,
} from '@scale/scaleui';
import { Languages } from '@scale/shared/types/demographics';
import { Attachment, convertMultimodalAssetToAttachment } from '@scale/shared/types/chat';
import FocusedImageModal from '../FocusedImageModal/FocusedImageModal';
import MMImageServiceAttachmentSearchResults from './MMImageServiceAttachmentSearchResults';
import SerpApiImageSearchResults from './SerpApiImageSearchResults';
import { useChatStoreExtract } from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.hooks';
import { Event, logEvent } from 'react-scale/lib/events';
import { MultimodalImageServiceEventType } from '@scale/shared/types/events/MultimodalImageServiceEvent';
import {
  IMAGES_PER_PAGE,
  MAX_PAGES,
  MultimodalImageServiceHook,
} from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useMultimodalImageService';
import { validatedSerpApiImageSearch } from '@scale/frontend-lib/dashboard-api/multimodal';
import { ReviewLevel } from '@scale/shared-types';
import _ from 'lodash';
import MMImageServiceAttachmentFilter from './MMImageServiceAttachmentFilter';
import { toast } from 'react-toastify';

type MMImageServiceAttachmentSelectorProps = {
  onSubmit: (attachment: Attachment) => void;
  disabled?: boolean;
  projectId: string;
  assignmentId?: string;
  multimodalImageServiceHook: MultimodalImageServiceHook;
  updateImageMetadata?: (url: string, metadata: MultimodalAssetDto) => void;
};

const isURL = (rawURL: string) => {
  try {
    const url = new URL(rawURL);
    return !!url;
  } catch {
    return false;
  }
};

// eslint-disable-next-line complexity
const MMImageServiceAttachmentSelector = ({
  onSubmit,
  disabled,
  projectId,
  assignmentId,
  multimodalImageServiceHook,
  updateImageMetadata,
}: MMImageServiceAttachmentSelectorProps) => {
  const {
    images,
    numHits,
    fetchImagesWithFilters,
    setLastUsedPage,
    searchStartTime,
    setTimeToSearch,
    setPreloadedImagesQuery,
    preloadedImagesQuery,
    incrementClickedOn,
    searchType,
    flagLowQuality,
    reviewLevel,
    claimImage,
  } = multimodalImageServiceHook;

  const [searchText, setSearchText] = useState('');
  const [malformedSearchText, setMalformedSearchText] = useState<string>('');
  const [dbPage, setDbPage] = useState<number>(1);
  const [focusedImage, setFocusedImage] = useState<MultimodalAssetDto | null>(null);
  const [serpPage, setSerpPage] = useState<number>(0);
  const [hasSearched, setHasSearched] = useState(false);
  const [isLoadingImageDbResults, setIsLoadingImageDbResults] = useState(false);
  const [showFilters, toggleShowFilters] = useToggle(false);
  const [tableMode, toggleTableMode] = useToggle(false);

  const config = useChatStoreExtract(state => state.config);
  const { imageValidatorSettings } = config;
  const enabledImageDatabases = useMemo(
    () => config.enabledImageDatabases ?? Object.values(ImageDatabaseSource),
    [config.enabledImageDatabases],
  );
  const [selectedImageDatabase, setSelectedImageDatabase] = useState<ImageDatabaseSource>(
    enabledImageDatabases.includes(ImageDatabaseSource.INTERNET)
      ? ImageDatabaseSource.INTERNET
      : ImageDatabaseSource.ELASTICSEARCH,
  );

  const categoriesCheckboxOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const name = event.target.name;

    setCategoriesCheckBoxOptions(prev => {
      return _.cloneDeep(prev).map(opt => {
        if (opt.id === name) {
          opt.checked = !opt.checked;
        }
        return opt;
      });
    });
  };

  const [categoriesCheckBoxOptions, setCategoriesCheckBoxOptions] = useState<CheckboxProps[]>(
    Object.values(ImageCategory)
      .filter((category: string) => {
        return category !== ImageCategory.MMMU && category !== ImageCategory.UNVALIDATED;
      })
      .map((category: string) => {
        return {
          id: category,
          label: category,
          checked: true,
          onChange: categoriesCheckboxOnChange,
        };
      }),
  );

  const languageCheckboxOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const name = event.target.name;

    setLanguageCheckBoxOptions(prev => {
      return _.cloneDeep(prev).map(opt => {
        if (opt.id === name) {
          opt.checked = !opt.checked;
        }
        return opt;
      });
    });
  };

  const [languageCheckBoxOptions, setLanguageCheckBoxOptions] = useState<CheckboxProps[]>([
    {
      id: 'no',
      label: 'No Language',
      checked: true,
      onChange: languageCheckboxOnChange,
    },
    ...Object.keys(Languages).map((category: string) => {
      return { id: category, label: category, checked: true, onChange: languageCheckboxOnChange };
    }),
  ]);

  const filters: ImageQueryFilters = useMemo(() => {
    const allowedCategories = categoriesCheckBoxOptions
      .filter((category: CheckboxProps) => {
        return category.checked;
      })
      .map((category: CheckboxProps) => {
        return category.id as ImageCategory;
      });

    let allowedLanguages = languageCheckBoxOptions
      .filter((category: CheckboxProps) => {
        return category.checked;
      })
      .map((category: CheckboxProps) => {
        return (category.id as string).toLowerCase();
      });
    // All are checked, so remove the filter
    if (allowedLanguages.length === languageCheckBoxOptions.length) {
      allowedLanguages = [];
    }

    const query_filters = {
      numResults: IMAGES_PER_PAGE,
      projectId,
      searchType,
      queryText: searchText,
      allowedCategories,
      allowedLanguages,
      ...imageValidatorSettings,
    };

    return query_filters;
  }, [
    searchText,
    projectId,
    imageValidatorSettings,
    searchType,
    categoriesCheckBoxOptions,
    languageCheckBoxOptions,
  ]);

  const numPages = Math.min(Math.ceil(numHits / IMAGES_PER_PAGE), MAX_PAGES);

  // Prefetching
  useEffect(() => {
    if (preloadedImagesQuery) {
      setSearchText(preloadedImagesQuery);
      setPreloadedImagesQuery('');
    }
  }, [preloadedImagesQuery, setPreloadedImagesQuery]);

  const [serpSearchText, setSerpSearchText] = useState<string>('');
  const serpQueryKey = ['serp', serpSearchText, serpPage];
  const {
    data: serpData,
    isError: isSerpError,
    isLoading: isLoadingSerp,
  } = useQuery(
    serpQueryKey,
    async () => {
      // protected by the enabled field
      if (serpSearchText === '') {
        throw new Error();
      }
      const results = await validatedSerpApiImageSearch(filters, config, serpPage);
      return results;
    },
    {
      enabled: serpSearchText !== '',
      staleTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  );

  const setPageAndFetchImages = async (newPage: number, isNewQuerySearch = false) => {
    if (isNewQuerySearch) {
      setSelectedImageDatabase(
        enabledImageDatabases.includes(ImageDatabaseSource.INTERNET)
          ? ImageDatabaseSource.INTERNET
          : ImageDatabaseSource.ELASTICSEARCH,
      );
    }

    if (enabledImageDatabases.includes(ImageDatabaseSource.ELASTICSEARCH)) {
      setIsLoadingImageDbResults(true);

      // imperative fetch images from db
      await fetchImagesWithFilters({ ...filters, from: (newPage - 1) * IMAGES_PER_PAGE });

      // imperative set db state
      setIsLoadingImageDbResults(false);
      setDbPage(newPage);
      setLastUsedPage(newPage);
    }

    // log
    if (isNewQuerySearch && assignmentId) {
      await logEvent(Event.MultimodalImageServiceEvent, {
        type: MultimodalImageServiceEventType.IMAGE_SEARCH,
        assignmentId,
        projectId,
        searchQuery: searchText,
        searchType: filters.searchType,
      });
    }
  };

  const onBeginSearch = async () => {
    const blockedKeywords =
      imageValidatorSettings?.blockedKeywordList?.filter(word =>
        searchText.split(' ').includes(word),
      ) || [];

    if (isURL(searchText)) {
      setMalformedSearchText('The search query should not contain any URL.');
    } else if (blockedKeywords.length > 0) {
      setMalformedSearchText(
        `Search query contains blocked keywords: ${blockedKeywords.join(',')}`,
      );
    } else {
      if (enabledImageDatabases.includes(ImageDatabaseSource.INTERNET)) {
        setSerpSearchText(searchText);
        setSerpPage(0);
      }

      setMalformedSearchText('');
      setHasSearched(true);
      setPageAndFetchImages(1, true);
    }
  };

  return (
    <div className="flex flex-col gap-2 w-full max-h-full">
      <div className="flex justify-between w-full gap-2">
        <TextField
          description={
            malformedSearchText.length > 0
              ? `Please enter a valid search term. ${malformedSearchText}`
              : ''
          }
          error={malformedSearchText.length > 0}
          className="flex-grow max-w-full"
          leadingIcon="search"
          placeholder="Enter an image search query, e.g., 'graph' or 'animal'"
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          onKeyDown={async e => {
            if (searchText.length && e.key === 'Enter') {
              onBeginSearch();
            }
          }}
          data-testid="image-search"
        />

        <Icon icon={['fas', 'filter-list']} onClick={() => toggleShowFilters()} />
        <Button
          variant="primary"
          type="submit"
          className="h-fit p-2 rounded-md"
          size="lg"
          onClick={async () => onBeginSearch()}
          disabled={searchText.length === 0}
          data-testid="submit-image-search-button"
        >
          Search
        </Button>
        <>
          <SegmentedButtons
            buttons={[
              { id: ImageDatabaseSource.ELASTICSEARCH, label: 'Image Database' },
              { id: ImageDatabaseSource.INTERNET, label: 'Internet' },
            ]}
            selected={selectedImageDatabase}
            onSelect={id => {
              setSelectedImageDatabase(id as ImageDatabaseSource);
            }}
            disabled={
              !hasSearched ||
              (selectedImageDatabase === ImageDatabaseSource.INTERNET && isLoadingSerp) ||
              (selectedImageDatabase === ImageDatabaseSource.ELASTICSEARCH &&
                isLoadingImageDbResults) ||
              // this logic needs to be updated if we add more databases
              enabledImageDatabases.length === 1
            }
          />
        </>
      </div>
      {showFilters && (
        <div>
          <div>
            <div className="inline-block mr-2" style={{ verticalAlign: 'top' }}>
              <MMImageServiceAttachmentFilter
                title="Filter Categories"
                options={categoriesCheckBoxOptions}
                setOptions={setCategoriesCheckBoxOptions}
              />
            </div>
            <div className="inline-block ml-2" style={{ verticalAlign: 'top' }}>
              <MMImageServiceAttachmentFilter
                title="Filter Language"
                options={languageCheckBoxOptions}
                setOptions={setLanguageCheckBoxOptions}
              />
            </div>
          </div>
          <Button
            onClick={() => {
              toggleShowFilters();
              onBeginSearch();
            }}
            variant="success"
          >
            Apply
          </Button>
        </div>
      )}
      {categoriesCheckBoxOptions.some((category: CheckboxProps) => category.checked) && (
        <div className="flex flex-row">
          <div className="text-lg pr-1">Active Categories:</div>
          {categoriesCheckBoxOptions
            .filter((category: CheckboxProps) => category.checked)
            .map((category: CheckboxProps) => {
              return category.id as ImageCategory;
            })
            .map((category: ImageCategory) => {
              return (
                <Tag key={category} className="mr-1">
                  {category}
                </Tag>
              );
            })}
        </div>
      )}

      {!hasSearched && <div className="text-2xl mt-2">Search our database for images.</div>}
      {hasSearched && (
        <>
          {selectedImageDatabase === ImageDatabaseSource.INTERNET ? (
            <SerpApiImageSearchResults
              imageData={serpData}
              disabled={disabled}
              isLoading={isLoadingSerp}
              isError={isSerpError}
              setFocusedImage={setFocusedImage}
              page={serpPage}
              setPage={page => {
                setSerpPage(page);
              }}
            />
          ) : (
            <>
              <div className="flex">
                <Switch
                  label="Table View"
                  labelPlacement="left"
                  checked={tableMode}
                  className="ml-auto"
                  onCheckedChange={() => {
                    toggleTableMode();
                  }}
                />
              </div>
              <MMImageServiceAttachmentSearchResults
                images={images}
                disabled={disabled}
                setFocusedImage={setFocusedImage}
                incrementClickedOn={incrementClickedOn}
                page={dbPage}
                setPage={setPageAndFetchImages}
                numPages={numPages}
                tableMode={tableMode}
                isLoading={isLoadingImageDbResults}
              />
            </>
          )}
        </>
      )}

      {focusedImage != null && (
        <FocusedImageModal
          image={focusedImage}
          onCancel={() => setFocusedImage(null)}
          onConfirm={async () => {
            if (searchStartTime) {
              setTimeToSearch(Date.now() - searchStartTime);
            }

            const result = await claimImage(focusedImage.image_url);
            if (result.found) {
              toast.error('Image already claimed', { position: 'bottom-right' });
            } else {
              onSubmit(convertMultimodalAssetToAttachment(focusedImage));
              setFocusedImage(null);
              updateImageMetadata?.(focusedImage.image_url, focusedImage);
            }
          }}
          flagLowQuality={reviewLevel !== ReviewLevel.Attempt ? flagLowQuality : undefined}
        />
      )}
    </div>
  );
};

export default MMImageServiceAttachmentSelector;
